import {Observable, ReplaySubject, Scheduler, Subject} from "rxjs";
import {QuoteViewModel, QuoteViewModels} from "../models/QuoteViewModel";
import {QuoteService} from "../service/QuoteService";
import {quoteHashCode} from "../util/QuoteStoreUtil";
import {MarketUtil, OutsideHoursUpdateInterval, PriceUpdateInterval} from "../../js/util/MarketUtil";
import {Optional} from "../../js/util/Optional";
import {copyRequest, QuoteRequest, QuoteRequests} from "../models/QuoteRequest";
import {QuoteRecords} from "../models/QuoteRecord";
import {ClientEventService} from "../../services/client-events/ClientEventService";
import {ClientEventType} from "../../services/client-events/models/ClientEventType";
import {QuoteModelFactory} from "../service/QuoteModelFactory";
import {CurrentUserService} from "../../services/user/CurrentUserService";

export class QuoteStore {

    private readonly sources = new Map<string, ReplaySubject<QuoteViewModel>>();
    private readonly hashes = new Map<string, QuoteRequest>();

    private readonly quotes$ = new ReplaySubject<QuoteRecords>(1);
    private readonly quoteListUpdated$ = new Subject<void>();

    private readonly pricesUpdated$ = new Subject<void>();

    private readonly stop$: Observable<boolean>;

    public constructor(private readonly quoteService: QuoteService,
                       private readonly clientEventService: ClientEventService,
                       private readonly quoteModelFactory: QuoteModelFactory,
                       private readonly currentUserService: CurrentUserService) {
        this.stop$ = this.buildStopPolling();
        this.init();
    }

    private buildStopPolling(): Observable<boolean> {
        return this.currentUserService
                   .current
                   .map((c) => c.isLoggedIn)
                   .distinctUntilChanged()
                   .filter((loggedIn) => !loggedIn);
    }

    private init() {
        this.watchQuotes();
        this.startUpdateInterval();
        this.watchQuoteListUpdated();
        this.clientEventService
            .events()
            .filter((evt) => evt.type === ClientEventType.BROKER_STATE_CHANGED)
            .takeUntil(this.stop$)
            .subscribe(() => this.updatePrices());
    }

    private watchQuotes() {
        this.quotes$
            .observeOn(Scheduler.async)
            .startWith([])
            .pairwise()
            .map(([previous, next]) => this.quoteModelFactory.buildViewModels(next, previous))
            .do((models) => this.updateModelSources(models))
            .subscribe(() => this.pricesUpdated$.next());
    }

    private startUpdateInterval() {
        const delay = () => Observable.of(1).delay(this.updateDelay());
        this.pricesUpdated$
            .observeOn(Scheduler.async)
            .switchMap(delay)
            .takeUntil(this.stop$)
            .subscribe(() => this.updatePrices());
    }

    private updateDelay(): number {
        if (MarketUtil.extendedMarketIsOpen()) {
            return PriceUpdateInterval;
        } else {
            return OutsideHoursUpdateInterval;
        }
    }

    /*
     * Used so when there is a first connection, or a large number of order updates, we don't spam a
     * lot of single requests to the server, and instead wait a small amount of time and then send them all
     */
    private watchQuoteListUpdated() {
        this.quoteListUpdated$
            .observeOn(Scheduler.async)
            .auditTime(100)
            .takeUntil(this.stop$)
            .subscribe(() => this.updatePrices(true));
    }

    /* Force is to make sure we get at least one quote if we're outside market hours */
    private updatePrices(force: boolean = false) {
        if ((this.sources.size === 0) && !force) {
            this.pricesUpdated$.next();
        } else {
            const codes = Array.from(this.hashes.values());
            Observable.of(codes)
                      .flatMap((c) => this.quoteService.quote(c))
                      .catch((err) => Observable.of([])) // Ignore errors
                      .subscribe((quotes) => this.quotes$.next(quotes));
        }
    }

    private updateModelSources(models: ReadonlyArray<QuoteViewModel>) {
        models.forEach((model) => {
            Optional.of(model)
                    .filter((m) => m.isValid)
                    .map((m) => m.hashCode)
                    .map((h) => this.sources.get(h))
                    .ifPresent((source$) => source$.next(model));
        });
    }

    /**
     * Returns an accumulating list of quotes, replacing the most recent quote for each code as it comes in and then
     * returning the entire list of most recent quotes that it has received
     */
    public scan(requests: QuoteRequests): Observable<QuoteViewModels> {
        return this.streamList(requests)
                   .scan((acc, next) => this.mergeQuotes(acc, next), []);
    }

    private mergeQuotes(quotes: QuoteViewModels, quote: QuoteViewModel): QuoteViewModels {
        const filtered = quotes.filter((q) => q.hashCode !== quote.hashCode);
        return [quote].concat(filtered);
    }

    public stream(requestOrRequests: QuoteRequest | QuoteRequests): Observable<QuoteViewModel> {
        if (Array.isArray(requestOrRequests)) {
            return this.streamList(requestOrRequests);
        } else {
            return this.streamSingle(requestOrRequests as QuoteRequest);
        }
    }

    private streamList(requestOrRequests: QuoteRequests): Observable<QuoteViewModel> {
        const sources = requestOrRequests.map((i) => this.streamSingle(i));
        return Observable.merge(...sources);
    }

    private streamSingle(request: QuoteRequest): Observable<QuoteViewModel> {
        const copy = copyRequest(request);
        return this.sourceFor(copy)
                   .observeOn(Scheduler.async)
                   .map((model) => model.clone())
                   .distinctUntilChanged((a, b) => a.asAt === b.asAt)
                   .finally(() => this.sourceUnsubscribed(copy));
    }

    private sourceFor(instrument: QuoteRequest): Observable<QuoteViewModel> {
        const hash = quoteHashCode(instrument);
        const existing = this.sources.get(hash);
        if (existing != null) {
            return existing;
        } else {
            const source = new ReplaySubject<QuoteViewModel>(1);
            this.sources.set(hash, source);
            this.hashes.set(hash, instrument);
            this.quoteListUpdated$.next();
            return source;
        }
    }

    private sourceUnsubscribed(instrument: QuoteRequest) {
        // Run in a setTimeout just in case this gets run before the change to the observers list happens
        setTimeout(() => {
            const hash = quoteHashCode(instrument);
            const source = this.sources.get(hash);
            if ((source != null) && (source.observers.length === 0)) {
                this.sources.delete(hash);
                this.hashes.delete(hash);
            }
        });
    }
}
